import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { aiGuardQueries } from '../lib/queries';

export type ScanDepth = 'fast' | 'deep';

/** The two scan depths, worded the same everywhere a scan can be started. */
export const SCAN_DEPTHS: { value: ScanDepth; label: string; hint: string }[] = [
  { value: 'fast', label: 'Fast scan', hint: 'Primary domain only. Finishes in a couple of minutes.' },
  { value: 'deep', label: 'Deep scan', hint: 'Every discovered subdomain. Thorough, takes longer.' },
];

const sans = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/**
 * Starts a scan of one vendor at the depth the user picks. The same queued, tenant-checked path everywhere
 * (POST /vendors/{id}/trigger-scan?scan_type=...); by default it opens the scan's progress page.
 */
export function useVendorScan(onStarted?: (scanId: string | undefined, depth: ScanDepth) => void) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ vendorId, depth }: { vendorId: string; depth: ScanDepth }) =>
      (await api.post(`/v1/vendors/${vendorId}/trigger-scan`, null, { params: { scan_type: depth } })).data as { scan_id?: string },
    onSuccess: (data, { depth }) => {
      if (onStarted) onStarted(data?.scan_id, depth);
      else if (data?.scan_id) navigate(`/scans/${data.scan_id}/progress`);
    },
    meta: { invalidates: [['scans'], ['vendors']] },
  });
}

/**
 * A scan button that asks how deep to scan before starting. Clicking it opens a small menu with Fast scan
 * and Deep scan; nothing starts until one is picked. Style the button like its neighbours via `buttonStyle`.
 */
export default function ScanTypeButton({
  label, onPick, isDark, buttonStyle, busy = false, busyLabel = 'Starting…', disabled = false,
  align = 'right', placement = 'bottom', block = false, title,
}: {
  label: React.ReactNode;
  onPick: (depth: ScanDepth) => void;
  isDark: boolean;
  buttonStyle?: React.CSSProperties;
  busy?: boolean;
  busyLabel?: string;
  disabled?: boolean;
  align?: 'left' | 'right';
  placement?: 'bottom' | 'top';
  block?: boolean;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // Every scan-start button in the app renders through here, so checking this once covers all of
  // them: a tenant with no verified-working AI provider is blocked server-side regardless (see
  // backend/middleware/ai_guard.py), but showing the explanation the moment they click -- instead
  // of after picking a depth and getting a 412 -- is a better place for it to land.
  const aiStatus = useQuery({ ...aiGuardQueries.status(), enabled: !disabled && !busy });
  const aiBlocked = aiStatus.data?.configured === false;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const c = {
    surface: isDark ? '#141414' : '#FFFFFF',
    hover: isDark ? '#1f1f1f' : '#F1F5F9',
    line: isDark ? '#2a2a2a' : '#E2E8F0',
    ink: isDark ? '#F1F5F9' : '#0F172A',
    muted: isDark ? '#94A3B8' : '#64748B',
    accent: isDark ? '#60A5FA' : '#185FA5',
    fastBg: isDark ? '#0c2a4a' : '#E6F1FB',
    deepBg: isDark ? '#211d4a' : '#EEEDFE',
    deepFg: isDark ? '#AFA9EC' : '#534AB7',
  };

  const pick = (depth: ScanDepth) => { setOpen(false); onPick(depth); };

  return (
    <div ref={wrap} style={{ position: 'relative', display: block ? 'flex' : 'inline-flex', flex: block ? 1 : undefined, minWidth: 0 }}>
      <button
        type="button" title={aiBlocked ? (aiStatus.data?.reason || 'An AI provider is required before scans can run.') : title}
        aria-haspopup="menu" aria-expanded={open}
        disabled={busy || disabled}
        onClick={() => {
          if (aiBlocked && aiStatus.data) {
            window.dispatchEvent(new CustomEvent('fluxwatch:ai-blocked', {
              detail: { aiMode: aiStatus.data.ai_mode, message: aiStatus.data.reason || '' },
            }));
            return;
          }
          setOpen(o => !o);
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: busy || disabled ? 'default' : 'pointer',
          opacity: busy || disabled ? 0.6 : 1, ...buttonStyle, ...(block ? { flex: 1, width: '100%' } : {}),
        }}>
        {busy ? busyLabel : label}
        {!busy && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', zIndex: 1400, width: 290, padding: 6, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10,
          boxShadow: isDark ? '0 10px 30px rgba(0,0,0,.6)' : '0 10px 28px rgba(15,23,42,.14)', fontFamily: sans,
          [align === 'right' ? 'right' : 'left']: 0,
          ...(placement === 'bottom' ? { top: 'calc(100% + 6px)' } : { bottom: 'calc(100% + 6px)' }),
        }}>
          {SCAN_DEPTHS.map(d => (
            <button key={d.value} role="menuitem" type="button" onClick={() => pick(d.value)}
              onMouseEnter={e => (e.currentTarget.style.background = c.hover)} onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              style={{ display: 'flex', gap: 11, width: '100%', textAlign: 'left', padding: '10px 11px', borderRadius: 7, border: 'none', background: 'none', cursor: 'pointer', fontFamily: sans }}>
              <span style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: d.value === 'deep' ? c.deepBg : c.fastBg, color: d.value === 'deep' ? c.deepFg : c.accent,
              }}>
                {d.value === 'deep' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
                )}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: c.ink }}>{d.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: c.muted, marginTop: 2, lineHeight: 1.4 }}>{d.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
